import CloseIcon from '@mui/icons-material/Close';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import classNames from 'classnames';
import { ReactNode } from 'react';

import { PortalDialogActions } from './portal-dialog-actions';
import { PortalDialogContent } from './portal-dialog-content';
import { PortalDialogTitle } from './portal-dialog-title';
import styles from './portal-dialog.module.css';

type MuiDialogProps = Omit<
  DialogProps,
  'maxWidth' | 'title' | 'content' | 'children' | 'onClose'
>;

interface CommonProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClose?: () => void;
  'data-testid'?: string;
}

interface PortalDialogPropsWithChildren extends CommonProps, MuiDialogProps {
  children: ReactNode;
  isShowCloseButton?: boolean;
}

export interface PortalDialogPropsWithChildrenProps
  extends CommonProps,
    MuiDialogProps {
  title: ReactNode;
  titleIcon?: ReactNode;
  content?: ReactNode;
  actions?: ReactNode;
  isShowCloseButton?: boolean;
}

export type PortalDialogProps =
  | PortalDialogPropsWithChildren
  | PortalDialogPropsWithChildrenProps;

export function PortalDialog(props: PortalDialogProps) {
  const {
    size = 'md',
    isShowCloseButton,
    'data-testid': dataTestId,
    PaperProps,
    ...restProps
  } = props;

  const [children, restPropsOfDialog] = (() => {
    if ('children' in restProps) {
      const { children, ...restPropsOfDialog } = restProps;
      return [children, restPropsOfDialog];
    } else {
      const { title, titleIcon, content, actions, ...restPropsOfDialog } =
        restProps;
      return [
        <>
          <PortalDialogTitle title={title} titleIcon={titleIcon} />
          {content && <PortalDialogContent>{content}</PortalDialogContent>}
          {actions && <PortalDialogActions>{actions}</PortalDialogActions>}
        </>,
        restPropsOfDialog,
      ];
    }
  })();
  return (
    <Dialog
      {...restPropsOfDialog}
      onClose={(e, reason) => {
        if (reason !== 'backdropClick') {
          if (restPropsOfDialog.onClose) {
            restPropsOfDialog.onClose();
          }
        }
      }}
      className={classNames(
        styles['overwrite'],
        styles['dialog'],
        restPropsOfDialog.className
      )}
      maxWidth={size}
      scroll="body"
      PaperProps={{
        className: styles['overwrite-paper'],
        ...PaperProps,
        'data-testid': dataTestId,
      }}
      data-size={size}
    >
      {isShowCloseButton && (
        <div
          className="p-4 absolute top-1 right-1 cursor-pointer"
          onClick={() => {
            if (restPropsOfDialog.onClose) {
              restPropsOfDialog.onClose();
            }
          }}
        >
          <CloseIcon className="w-[22px] h-auto" />
        </div>
      )}
      {children}
    </Dialog>
  );
}

export default PortalDialog;
